import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { Store } from '@ngrx/store';
import { tap, filter, take, switchMap, catchError, map, withLatestFrom } from 'rxjs/operators';

import * as fromStore from '../store';
import * as fromBlogs from '../../blogs/store';
import * as fromRoot from '../../store';
import { getSettings } from '../../settings/store';

import { Observable, of } from 'rxjs';
import { getSelectedOverviewPage } from '../store';
import { SetTags, SetTitle } from '../../store';
import { StringUtility } from '@teamfoster/sdk/text-utility';
import { FromDictionaryPipe } from '@teamfoster/sdk/dictionary-ngrx';

@Injectable({ providedIn: 'root' })
export class ContentOverviewGuard {
  private store = inject<Store<fromStore.ContentState>>(Store);
  #dict = new FromDictionaryPipe();


  canDeactivate(component: any, currentRoute: ActivatedRouteSnapshot): Observable<boolean> {
    return this.checkDeactivateStore(currentRoute).pipe(
      switchMap(() => of(true)),
      catchError(() => of(false))
    );
  }

  checkDeactivateStore(currentRoute: ActivatedRouteSnapshot): Observable<boolean> {
    return this.store.select(fromStore.getSelectedOverviewLoaded).pipe(
      tap(loaded => {
        const contentType: string = currentRoute.params['contentType'] || currentRoute.data['contentType'];
        switch (contentType.toLowerCase()) {
          case 'pagina':
            break;
          case 'evenementen':
            this.store.dispatch(fromStore.ResetEvents());
            break;
          case 'nieuws':
            this.store.dispatch(fromStore.ResetNews());
            break;
          case 'blogs':
            this.store.dispatch(fromBlogs.ResetBlogs());
            break;
        }
      }),
      filter(loaded => !loaded),
      map(loaded => !loaded),
      take(1)
    );
  }

  canActivate(): Observable<boolean> {
    return this.checkStore().pipe(
      switchMap(() => of(true)),
      catchError(() => of(false))
    );
  }

  checkStore(): Observable<boolean> {
    return this.store.select(fromStore.getSelectedOverviewLoaded).pipe(
      withLatestFrom(
        this.store.select(fromRoot.getRouterState),
        this.store.select(getSelectedOverviewPage),
        this.store.select(getSettings)
      ),
      tap(([loaded, router, page, settings]) => {
        if (
          !loaded ||
          (+router.state.queryParams?.['page'] || 0) !== page!.current ||
          (+router.state.queryParams?.['tag'] || 0) !== page!.selectedTag ||
          (router.state.queryParams?.['q'] || '') !== page!.query
        ) {
          const p = +router.state.queryParams?.['page'] || 0;
          const contentType: string = router.state.params?.['contentType'] || router.state.data?.['contentType'];
          switch (contentType.toLowerCase()) {
            case 'pagina':
              this.store.dispatch(fromStore.LoadContentPages());
              break;
            case 'evenementen':
              this.store.dispatch(
                fromStore.LoadEventsOverview({
                  skip: p * page!.size,
                  take: page!.size,
                })
              );
              break;
            case 'nieuws':
              this.store.dispatch(
                fromStore.LoadNewsOverview({
                  skip: p * page!.size,
                  take: page!.size,
                })
              );
              break;
            case 'blogs':
              this.store.dispatch(
                fromBlogs.LoadBlogsOverview({
                  skip: p * page!.size,
                  take: page!.size,
                })
              );
              break;
          }

          const key = contentType.toLowerCase();
          const title = `${(this.#dict.transform(`meta-overview-${key}-title`, false) || this.#dict.transform(`${key}-overzicht-titel`))} | ${this.#dict.transform('meta-title-suffix')}`;
          const description = StringUtility.Truncate(
            StringUtility.StripHTML(this.#dict.transform(`meta-overview-${key}-description`, false) || this.#dict.transform(`${key}-overzicht-intro-tekst`)),
            157,
            '...'
          );
          this.store.dispatch(SetTitle({ title }));
          this.store.dispatch(
            SetTags({
              properties: {
                'description': description,
                'og:title': title,
                'og:image': settings?.homeMetaData.imageUrl || '',
                'og:description': description,
              },
            })
          );
        }
      }),
      map(([loaded, router]) => loaded),
      filter(loaded => loaded!),
      take(1)
    );
  }
}
